import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { supportDisplayRef } from '../lib/supportDisplayRef';
import { LifebuoyIcon } from '@heroicons/react/24/outline';

export interface SupportRequestRow {
  _id: string;
  orderId?: string;
  subject: string;
  status: string;
  lastMessage?: string;
  createdAt: string;
  updatedAt: string;
  user?: { _id: string; name?: string; email?: string };
  order?: { totalPriceEGP?: number; status?: string; createdAt?: string; orderNumber?: string };
}

export default function SupportRequestsTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const navigate = useNavigate();
  const [rows, setRows] = useState<SupportRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [orderNumberFilter, setOrderNumberFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [appliedStatus, setAppliedStatus] = useState('');
  const [appliedUser, setAppliedUser] = useState('');
  const [appliedOrderNumber, setAppliedOrderNumber] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');

  useEffect(() => {
    load();
  }, [page, appliedStatus, appliedUser, appliedOrderNumber, appliedDateFrom, appliedDateTo]);

  const load = async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page, limit };
      if (appliedStatus) params.status = appliedStatus;
      if (appliedUser.trim()) params.user = appliedUser.trim();
      if (appliedOrderNumber.trim()) params.orderNumber = appliedOrderNumber.trim();
      if (appliedDateFrom) params.dateFrom = appliedDateFrom;
      if (appliedDateTo) params.dateTo = appliedDateTo;
      const res = await api.get('/admin/support/requests', { params });
      setRows(res.data.data || []);
      setPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (s: string) => {
    try {
      return new Date(s).toLocaleString();
    } catch {
      return s;
    }
  };

  const statusLabel = (s: string) => {
    switch (s) {
      case 'pending':
        return t('supportRequests.statusPending');
      case 'in_progress':
        return t('supportRequests.statusInProgress');
      case 'resolved':
        return t('supportRequests.statusResolved');
      default:
        return s;
    }
  };

  const applyDraftFilters = () => {
    setAppliedStatus(statusFilter);
    setAppliedUser(userFilter.trim());
    setAppliedOrderNumber(orderNumberFilter.trim());
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setPage(1);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setUserFilter('');
    setOrderNumberFilter('');
    setDateFrom('');
    setDateTo('');
    setAppliedStatus('');
    setAppliedUser('');
    setAppliedOrderNumber('');
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setPage(1);
  };

  if (loading && rows.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange" />
        <p className="mt-4 text-gray-600">{t('supportRequests.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className={`px-4 py-3 border-b border-gray-200 flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <LifebuoyIcon className="w-6 h-6 text-primary-orange" />
          <h2 className="text-lg font-semibold text-gray-800">{t('supportRequests.title')}</h2>
        </div>

        <div className={`p-4 flex flex-wrap gap-3 border-b border-gray-100 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">{t('supportRequests.allStatuses')}</option>
            <option value="pending">{t('supportRequests.statusPending')}</option>
            <option value="in_progress">{t('supportRequests.statusInProgress')}</option>
            <option value="resolved">{t('supportRequests.statusResolved')}</option>
          </select>
          <input
            type="text"
            placeholder={t('supportRequests.filterUser')}
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[140px]"
          />
          <input
            type="text"
            placeholder={t('supportRequests.filterOrderNumber')}
            value={orderNumberFilter}
            onChange={(e) => setOrderNumberFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[140px]"
          />
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={applyDraftFilters}
            className="px-4 py-2 text-sm rounded-lg bg-primary-orange text-white hover:opacity-90"
          >
            {t('supportRequests.search')}
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
          >
            {t('supportRequests.clearFilters')}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>
                  {t('supportRequests.colId')}
                </th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>
                  {t('supportRequests.colUser')}
                </th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>
                  {t('supportRequests.colOrder')}
                </th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>
                  {t('supportRequests.colStatus')}
                </th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>
                  {t('supportRequests.colLastMessage')}
                </th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>
                  {t('supportRequests.colCreated')}
                </th>
                <th className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${isRtl ? 'text-right' : 'text-left'}`}>
                  {t('supportRequests.colUpdated')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((r) => (
                <tr
                  key={r._id}
                  className="hover:bg-orange-50/40 cursor-pointer transition-colors"
                  onClick={() => navigate(`/support-requests/${r._id}`)}
                >
                  <td className="px-4 py-3 text-sm font-semibold text-[#E85D2A] tabular-nums">
                    #{supportDisplayRef(r._id)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {r.user?.name || r.user?.email || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800 tabular-nums">
                    {r.order?.orderNumber ? `#${r.order.orderNumber}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium transition-all ${
                        r.status === 'resolved'
                          ? 'bg-green-100 text-green-800'
                          : r.status === 'in_progress'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-amber-100 text-amber-900'
                      }`}
                    >
                      {statusLabel(r.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{r.lastMessage || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{fmt(r.createdAt)}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{fmt(r.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && !loading && (
          <p className="text-center py-8 text-gray-500">{t('supportRequests.empty')}</p>
        )}

        {total > limit && (
          <div className={`flex items-center justify-between px-4 py-3 border-t border-gray-200 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <span className="text-sm text-gray-600">
              {t('supportRequests.pageOf', { page, pages, total })}
            </span>
            <div className={`flex gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 rounded border text-sm disabled:opacity-40"
              >
                {t('supportRequests.prev')}
              </button>
              <button
                type="button"
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded border text-sm disabled:opacity-40"
              >
                {t('supportRequests.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
